import { cs } from '../lib/currency';
import { useState, useRef } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Download, Upload, FileSpreadsheet, AlertCircle, CheckCircle, Mail } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

export const BulkActions = ({ 
  entityName, 
  exportEndpoint, 
  uploadEndpoint, 
  onUploadComplete,
  sampleFields = [],
  customResultsRenderer = null,  // Custom renderer for specialized results
  uploadButtonLabel = "Import",  // Customizable button label
  dialogTitle = null,  // Custom dialog title
  dialogDescription = null,  // Custom dialog description
  showExport = true,  // Option to hide export button
  showWelcomeEmailOption = false  // Show welcome email checkbox for parent/user imports
}) => {
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const fileInputRef = useRef(null);

  const handleExport = async () => {
    try {
      const response = await api.get(exportEndpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${entityName.toLowerCase()}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${entityName} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${entityName.toLowerCase()}`);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    // Build URL with query params for welcome email option
    let url = uploadEndpoint;
    if (showWelcomeEmailOption) {
      url = `${uploadEndpoint}?send_welcome_email=${sendWelcomeEmail}`;
    }

    try {
      const response = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResults(response.data);
      setShowUploadDialog(false);
      setShowResultsDialog(true);
      
      if (response.data.created > 0 || response.data.updated > 0) {
        onUploadComplete?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadSampleCSV = () => {
    const headers = sampleFields.join(',');
    const sampleRow = sampleFields.map(f => `sample_${f}`).join(',');
    const csvContent = `${headers}\n${sampleRow}`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${entityName.toLowerCase()}_template.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <>
      <div className="flex gap-2">
        {showExport && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleExport}
            data-testid={`export-${entityName.toLowerCase()}`}
          >
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
        )}
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setShowUploadDialog(true)}
          data-testid={`import-${entityName.toLowerCase()}`}
        >
          <Upload className="h-4 w-4 mr-1" />
          {uploadButtonLabel}
        </Button>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialogTitle || `Import ${entityName}`}</DialogTitle>
            <DialogDescription>
              {dialogDescription || `Upload a CSV file to bulk create or update ${entityName.toLowerCase()}.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              <FileSpreadsheet className="h-10 w-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-3">
                Select a CSV file to upload
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="csv-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Select CSV File'}
              </Button>
            </div>

            {sampleFields.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Required Columns:</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {sampleFields.slice(0, 8).map(field => (
                    <Badge key={field} variant="secondary" className="text-xs">
                      {field}
                    </Badge>
                  ))}
                  {sampleFields.length > 8 && (
                    <Badge variant="outline" className="text-xs">
                      +{sampleFields.length - 8} more
                    </Badge>
                  )}
                </div>
                <Button variant="link" size="sm" className="p-0 h-auto" onClick={downloadSampleCSV}>
                  Download template CSV
                </Button>
              </div>
            )}

            {/* Welcome Email Option */}
            {showWelcomeEmailOption && (
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="send-welcome-email"
                    checked={sendWelcomeEmail}
                    onCheckedChange={setSendWelcomeEmail}
                    data-testid="bulk-upload-send-welcome-email"
                  />
                  <label 
                    htmlFor="send-welcome-email" 
                    className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"
                  >
                    <Mail className="h-4 w-4 text-blue-500" />
                    Send welcome email with login credentials to new accounts
                  </label>
                </div>
                <p className="text-xs text-slate-500 mt-2 ml-7">
                  {sendWelcomeEmail 
                    ? "New accounts will receive an email with their temporary password" 
                    : "You'll need to share credentials manually with new users"}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={showResultsDialog} onOpenChange={setShowResultsDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Results</DialogTitle>
          </DialogHeader>

          {uploadResults && (
            customResultsRenderer ? customResultsRenderer(uploadResults) : (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <CheckCircle className="h-6 w-6 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-700">{uploadResults.created}</p>
                  <p className="text-sm text-green-600">Created</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <CheckCircle className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-700">{uploadResults.updated || 0}</p>
                  <p className="text-sm text-blue-600">Updated</p>
                </div>
              </div>

              {/* Skipped items for bulk assignment */}
              {uploadResults.skipped > 0 && uploadResults.skipped_details && (
                <div className="bg-amber-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                    <p className="font-medium text-amber-700">
                      {uploadResults.skipped} Skipped (Already Enrolled)
                    </p>
                  </div>
                  <div className="max-h-32 overflow-y-auto text-sm text-amber-600">
                    {uploadResults.skipped_details.map((item, idx) => (
                      <p key={idx}>{item.player_name} - {item.program_name}</p>
                    ))}
                  </div>
                </div>
              )}

              {uploadResults.errors && uploadResults.errors.length > 0 && (
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <p className="font-medium text-red-700">
                      {uploadResults.errors.length} Error(s)
                    </p>
                  </div>
                  <div className="max-h-32 overflow-y-auto text-sm text-red-600">
                    {uploadResults.errors.map((err, idx) => (
                      <p key={idx}>Row {err.row}: {err.error}</p>
                    ))}
                  </div>
                </div>
              )}

              {uploadResults.new_accounts && uploadResults.new_accounts.length > 0 && (
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="font-medium text-amber-700 mb-2">
                    New Accounts Created (Save these credentials!)
                  </p>
                  <div className="max-h-32 overflow-y-auto text-sm">
                    {uploadResults.new_accounts.map((acc, idx) => (
                      <p key={idx} className="font-mono text-amber-800">
                        {acc.email}: {acc.temp_password}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Enrollments created for bulk assignment */}
              {uploadResults.enrollments && uploadResults.enrollments.length > 0 && (
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="font-medium text-green-700 mb-2">
                    Enrollments Created
                  </p>
                  <div className="max-h-48 overflow-y-auto text-sm space-y-1">
                    {uploadResults.enrollments.map((enrollment, idx) => (
                      <div key={idx} className="bg-white rounded p-2 border border-green-100">
                        <p className="font-medium text-slate-800">{enrollment.player_name}</p>
                        <p className="text-slate-600">{enrollment.program_name}</p>
                        <p className="text-xs text-slate-500">
                          Amount: {cs()}{enrollment.prorated_amount || enrollment.monthly_fee} 
                          {enrollment.prorated_amount && enrollment.prorated_amount !== enrollment.monthly_fee && 
                            <span className="text-green-600 ml-1">(prorated)</span>
                          }
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )
          )}

          <DialogFooter>
            <Button onClick={() => setShowResultsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkActions;
